import { FileSignature } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ROW_ACTION } from "@/features/accident-parties";
import { ContractNotice } from "@/features/contact-fields";
import { FirmOptions } from "@/features/firm-options";
import { SendContractDialog } from "@/features/send-contract";
import { ApiError } from "@/lib/api";
import { callAskedMessage, callContractAsk } from "@/lib/call-contract";
import type { WorkLead } from "@/lib/phone-work";
import { useContractCheck, useFirms, useLeadCard, useLeadFormFields, useUpdateContact } from "@/lib/queries";
import { detailInput, draftOf, fieldText, maxLength, parseDetail } from "@/lib/record-fields";
import type { ContractAsk } from "@/lib/send-contract";
import type { LeadCard, RecordField } from "@/lib/types";
import { dropCallFormAnswers, useCallForm } from "@/softphone/call-form-store";
import type { CardRow } from "@/softphone/lead-card";

/**
 * Send contract from the Phone page (ACL-560): the record's Send contract (ACL-418..541,
 * `features/send-contract.tsx`), offered on the call's card so a rep sends the agreement while the lead
 * is still on the line, without leaving the dialer. The rules are `lib/call-contract.ts`'s.
 *
 * The button is the card's (`SendContractButton`); the dialog is the page's (`CallContractDialog`), for
 * the lead the page works with, so it stays open when the call ends under it and the card is drawn
 * again for the outcome window. It sends to that lead and nobody else: the card offers the button only
 * when it shows the same lead, and the dialog is keyed by it.
 *
 * What the dialog lists to fix is marked on the call's form when it closes (`onAsk`), each value where
 * the form has it, and the ones the call's folders do not hold in "Needed for the contract" at the top
 * of the form (`softphone/call-form.tsx`): the e-mail and the name (`ContractDetailAnswer`), a minor's
 * guardian, and the firm when the lead has none (`ContractFirmAnswer`).
 *
 * Once core-api says every value the chosen agreement is filled with (ACL-604), the dialog shows them
 * for the rep to fix there, starting from the answers the call's form has not saved yet; the ones it
 * saves before the press are no longer the form's to send (`dropCallFormAnswers`).
 */

/** Send contract on the call's card: there only where the deployment has a contract service, as on
 *  the record. A press asks the check again, so a value just filled in on the form counts. At the
 *  card's top the card's one solid button (ACL-626): signing the lead is what the call is for. On the
 *  caller's row of the accident panel (`row`, ACL-671) it is that row's Send agreement, as on every
 *  other person's row: the same link, the words by where their agreement stands, gone once signed. */
export function SendContractButton({
  leadId, row = null, onPress,
}: {
  leadId: string;
  row?: CardRow | null;
  onPress: () => void;
}) {
  const check = useContractCheck(leadId);
  if (check.data === undefined || check.data.reason === "not_set_up") return null;
  const press = () => {
    void check.refetch();
    onPress();
  };
  if (row) {
    if (row.agreement?.state === "signed") return null;
    return (
      <button type="button" className={ROW_ACTION} onClick={press}>
        <FileSignature className="size-3" aria-hidden />
        {row.agreement?.state === "sent" ? "Resend agreement" : "Send agreement"}
      </button>
    );
  }
  return (
    <Button type="button" variant="default" size="sm" onClick={press}>
      <FileSignature />
      Send contract
    </Button>
  );
}

/**
 * The dialog for the page's lead. Closing it hands what it listed to the form (`onAsk`, null for
 * nothing), with a toast that names them, as the record does; a lead with no firm is asked for the firm.
 */
export function CallContractDialog({
  lead, open, onOpenChange, onAsk,
}: {
  lead: WorkLead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAsk: (ask: ContractAsk | null) => void;
}) {
  const toast = useToast();
  const card = useLeadCard(lead.id, lead.stay).data;
  const check = useContractCheck(lead.id);
  // The form's own read (one cache entry): what a wrong value held is marked against it. Only while the
  // dialog is open, which is when it closes: a call whose rep never presses it reads nothing more.
  const fields = useLeadFormFields(open ? lead.id : null);
  const { drafts } = useCallForm(lead.id);
  if (!card || card.id !== lead.id) return null;
  const textOf = (key: string) => {
    for (const section of fields.data?.sections ?? []) {
      const found = section.fields.find((f) => f.key === key);
      if (found) return fieldText(found);
    }
    return null;
  };
  return (
    <SendContractDialog
      open={open}
      onOpenChange={onOpenChange}
      contact={card}
      check={check.data}
      checking={check.isFetching}
      place="call"
      startDrafts={drafts}
      onSaved={(keys) => dropCallFormAnswers(lead.id, keys)}
      onClosed={(found) => {
        const ask = callContractAsk(found, check.data, textOf);
        onAsk(ask);
        if (!ask) return;
        const said = callAskedMessage(found);
        toast.error(said.title, said.detail);
      }}
    />
  );
}

/**
 * One of the contact's own details Send contract needs (the e-mail, the name), on the call's form: an
 * input saved through the contact (`PATCH /leads/{id}`), as the record's details panel saves it, when
 * the rep leaves it or presses Enter. The call's other answers are the form's and go through its queue
 * (`call-form-store.ts`); these are not the form's, so they save on their own. Checked first as the
 * record checks them (`parseDetail`), so half an address is said, not sent.
 */
export function ContractDetailAnswer({
  leadId, field, notice,
}: {
  leadId: string;
  field: RecordField;
  /** Why Send contract needs it, while it still does. */
  notice: string | null;
}) {
  const save = useUpdateContact();
  const held = () => {
    const value = draftOf(field);
    return typeof value === "string" ? value : "";
  };
  const [draft, setDraft] = React.useState(held);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  /** What was last sent: Enter and the blur after it are one save, whether or not it has come back. */
  const sent = React.useRef<string | null>(null);
  const id = `call-form-${field.key}`;
  const commit = () => {
    if (save.isPending || draft.trim() === held() || draft.trim() === sent.current) return;
    const parsed = parseDetail(field, draft);
    if (!parsed.ok) {
      setProblem(parsed.problem);
      return;
    }
    const input = detailInput(field.key, parsed.value);
    if (!input) return;
    sent.current = draft.trim();
    save.mutate(
      { id: leadId, ...input },
      {
        onSuccess: () => setSaved(true),
        onError: (e) => {
          sent.current = null;
          setProblem(e instanceof ApiError ? e.message : "Could not save. Please try again.");
        },
      },
    );
  };
  return (
    <Field
      label={field.title}
      htmlFor={id}
      error={problem ?? undefined}
      hint={notice ? <ContractNotice text={notice} /> : saved ? "Saved." : undefined}
    >
      <Input
        type={field.type === "email" ? "email" : "text"}
        value={draft}
        invalid={problem !== null}
        maxLength={maxLength(field)}
        autoComplete="off"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setDraft(e.target.value);
          setProblem(null);
          setSaved(false);
        }}
        onBlur={commit}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          commit();
        }}
      />
    </Field>
  );
}

/**
 * The lead's firm, picked on the call's form when Send contract found none (ACL-560): the record's
 * picker (the state's firms first, the preferred one at the top, ACL-538), saved the moment it is
 * picked, as Set firm saves it. A rep may set the firm (ACL-340).
 */
export function ContractFirmAnswer({ card, notice }: { card: LeadCard; notice: string | null }) {
  const firms = useFirms();
  const save = useUpdateContact();
  // What was picked here, shown until the card is read again with it.
  const [picked, setPicked] = React.useState<string | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);
  const id = "call-form-firm";
  const value = picked ?? (card.firm ? String(card.firm.id) : "");
  const where = card.state ? `The firms that take cases in ${card.state} come first, the preferred one at the top.` : undefined;
  return (
    <Field label="Firm" htmlFor={id} error={problem ?? undefined} hint={notice ? <ContractNotice text={notice} /> : where}>
      <Select
        value={value}
        onValueChange={(next) => {
          setPicked(next);
          setProblem(null);
          save.mutate(
            { id: card.id, firm_id: Number(next) },
            {
              onError: (e) => {
                setPicked(null);
                setProblem(e instanceof ApiError ? e.message : "Could not set the firm. Please try again.");
              },
            },
          );
        }}
      >
        <SelectTrigger id={id} aria-label="Firm" invalid={problem !== null}>
          <SelectValue placeholder="Choose a firm…" />
        </SelectTrigger>
        <SelectContent>
          <FirmOptions firms={firms.data ?? []} state={card.state} />
        </SelectContent>
      </Select>
    </Field>
  );
}
